/**
 * 2FA'ni sozlash: QR, qo'lda kiritiladigan kalit va 6 xonali kod; yoqilgach zaxira kodlar.
 * Ikki joyda: profildagi 2FA kartasi va majburiy sozlash sahifasi (pages/auth/TwoFactorSetupPage.tsx).
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import type { Me } from "@/features/auth/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/field";
import { toast } from "@/components/ui/toast";

export interface TotpSetup {
  secret: string;
  otpauth_uri: string;
}

export interface TotpEnabled {
  backup_codes: string[];
  user: Me;
}

export function TotpEnrollment({
  setup,
  qrVersion,
  onEnabled,
  onCancel,
  stacked,
}: {
  setup: TotpSetup;
  /** Har yangi sozlashda oshadi: QR rasmi qayta so'raladi (brauzer eski rasmni ko'rsatmasin) */
  qrVersion: number;
  onEnabled: (res: TotpEnabled) => void;
  onCancel?: () => void;
  /** Tor joyda: QR tepada, qadamlar ostida */
  stacked?: boolean;
}) {
  const { t } = useTranslation("profile");
  const { t: tc } = useTranslation();
  const [code, setCode] = useState("");
  const enable = useMutation({
    mutationFn: () => api.post<TotpEnabled>("/api/auth/2fa/enable/", { code }),
    onSuccess: (res) => {
      setCode("");
      onEnabled(res);
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : t("twofa.enableFailed")),
  });

  return (
    <div className={cn(stacked ? "flex flex-col items-center gap-4 text-left" : "grid grid-cols-[160px_1fr] gap-5")}>
      <div className="flex h-[160px] w-[160px] shrink-0 items-center justify-center rounded-[10px] border border-dashed border-border bg-white p-2">
        <QrCode version={qrVersion} />
      </div>
      <div className="w-full space-y-3 text-[13px]">
        <p>{t("twofa.step1")}</p>
        <p className="text-[12px] text-muted">
          {t("twofa.manual")} <span className="font-mono font-semibold text-ink">{setup.secret.match(/.{1,4}/g)?.join(" ")}</span>
        </p>
        <p>{t("twofa.step2")}</p>
        <form
          className="flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            enable.mutate();
          }}
        >
          <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" autoComplete="one-time-code" placeholder="123456" className="w-28 text-center font-mono" />
          <Button type="submit" variant="primary" loading={enable.isPending} disabled={code.length !== 6}>
            {t("twofa.enable")}
          </Button>
          {onCancel && (
            <Button variant="ghost" onClick={onCancel}>
              {tc("actions.cancel")}
            </Button>
          )}
        </form>
        <p className="text-[11px] text-muted">{t("twofa.backupHint")}</p>
      </div>
    </div>
  );
}

export function BackupCodes({ codes, onDone, doneLabel }: { codes: string[]; onDone: () => void; doneLabel?: string }) {
  const { t } = useTranslation("profile");
  return (
    <div className="rounded-[10px] border border-success bg-success-soft p-3 text-left text-[13px]">
      <div className="font-semibold">{t("twofa.backupTitle")}</div>
      <div className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1 font-mono text-[12px]">
        {codes.map((c) => (
          <span key={c}>{c}</span>
        ))}
      </div>
      <Button size="sm" className="mt-3" onClick={onDone}>
        {doneLabel ?? t("twofa.savedThem")}
      </Button>
    </div>
  );
}

/** QR: backend joriy sozlashning secret'idan o'zi chizadi (URL'da secret yo'q, tashqi xizmat yo'q) */
function QrCode({ version }: { version: number }) {
  const { t } = useTranslation("profile");
  return <img alt={t("twofa.qrAlt")} className="h-full w-full" src={`/api/auth/2fa/qr.svg?v=${version}`} />;
}
